// StaySearchFormMobileNR.tsx
import React, { FC, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import LocationInputSimple from "../../HeroSearchForm/LocationInputSimple";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "redux/store";
import {
  getPropertyType,
  getExclusiveProperty,
} from "../../../redux/reducers/Properties/PropertiesSlice";
import { setSearchData } from "redux/reducers/Search/SearchSlice";

export interface StaySearchFormMobileNRProps {
  onClose: () => void;
  screenType?: string;
}

const StaySearchFormMobileNR: FC<StaySearchFormMobileNRProps> = ({
  onClose,
  screenType,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const storedSearchData = useSelector(
    (state: RootState) => state.search.searchedData
  );

  const propertyTypes = useSelector(
    (state: RootState) => state.property.propertyTypes
  );

  // ── Exclusive + Featured properties from Redux ──────────────
  const exclusiveProperty = useSelector(
    (state: RootState) => state.property.exclusiveProperty
  );
  const propertiesLoading = useSelector(
    (state: RootState) => state.property.loading
  );

  const {
    locationData,
    latitude,
    longitude,
    propertyTypeId,
    priceMin,
    priceMax,
  } = storedSearchData || {};

  const [selectedLocation, setSelectedLocation] = useState<string>("");
  const [selectedRoomTypes, setSelectedRoomTypes] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 5000]);
  const [showPriceSlider, setShowPriceSlider] = useState(false);

  // ── Property type mapping ────────────────────────────────────
  const propertyTypeMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    const reverseMap: Record<string, string> = {};
    propertyTypes?.forEach((type: any) => {
      const normalizedName = type.name.toLowerCase();
      map[normalizedName] = String(type.id);
      reverseMap[String(type.id)] = normalizedName;
    });
    return { map, reverseMap };
  }, [propertyTypes]);

  // ── Fetch on mount ───────────────────────────────────────────
  useEffect(() => {
    if (!propertyTypes || propertyTypes.length === 0) {
      dispatch(getPropertyType());
    }
    // Always refresh properties when component mounts
    // These are fire-and-forget — Redux slice handles the response shape
    dispatch(getExclusiveProperty()).catch(() => {});
  }, [dispatch]);

  // ── Sync location from Redux ─────────────────────────────────
  useEffect(() => {
    if (locationData) {
      const locationString = locationData.neighborhood
        ? `${locationData.neighborhood}, ${locationData.city}, ${locationData.country}`
        : `${locationData.city}, ${locationData.country}`;
      setSelectedLocation(locationString);
    }
  }, [latitude, longitude, locationData]);

  // ── Sync property types from Redux ───────────────────────────
  useEffect(() => {
    if (!propertyTypeId) return;
    if (Object.keys(propertyTypeMap.reverseMap).length === 0) return;

    const typeIdsArray = propertyTypeId.split(",").filter(Boolean);
    const typeNames = typeIdsArray
      .map((id: string) => propertyTypeMap.reverseMap[id.trim()])
      .filter(Boolean);

    if (JSON.stringify(typeNames) !== JSON.stringify(selectedRoomTypes)) {
      setSelectedRoomTypes(typeNames);
    }
  }, [propertyTypeId, propertyTypeMap.reverseMap]);

  // ── Sync price range from Redux ───────────────────────────────
  useEffect(() => {
    if (priceMin !== undefined || priceMax !== undefined) {
      const newRange: [number, number] = [priceMin ?? 0, priceMax ?? 5000];
      if (priceRange[0] !== newRange[0] || priceRange[1] !== newRange[1]) {
        setPriceRange(newRange);
      }
    }
  }, [priceMin, priceMax]);

  // ── Handlers ─────────────────────────────────────────────────
  const handleRoomTypeClick = (e: React.MouseEvent, type: string) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedRoomTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleRangeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    isMin: boolean
  ) => {
    const value = Number(e.target.value);
    let newRange: [number, number];

    if (isMin) {
      if (value > priceRange[1]) return;
      newRange = [value, priceRange[1]];
    } else {
      if (value < priceRange[0]) return;
      newRange = [priceRange[0], value];
    }

    setPriceRange(newRange);
    dispatch(
      setSearchData({
        ...storedSearchData,
        priceMin: newRange[0],
        priceMax: newRange[1],
      })
    );
  };

  // ── Render property type buttons ─────────────────────────────
  const renderPropertyTypeButtons = () => {
    if (!propertyTypes || propertyTypes.length === 0) {
      return (
        <div className="text-sm text-neutral-500 dark:text-neutral-400">
          Loading property types...
        </div>
      );
    }
    return propertyTypes.map((type: any) => {
      const normalizedName = type.name.toLowerCase();
      const isSelected = selectedRoomTypes.includes(normalizedName);
      return (
        <button
          key={type.id}
          type="button"
          onClick={(e) => handleRoomTypeClick(e, normalizedName)}
          className={`px-3 py-2.5 text-sm font-semibold rounded-lg transition-all border-2 ${
            isSelected
              ? "bg-primary-700 border-primary-700 text-white shadow-md"
              : "bg-white dark:bg-neutral-800 border-neutral-300 dark:border-neutral-600 text-neutral-800 dark:text-neutral-200 hover:border-primary-500 dark:hover:border-primary-500"
          }`}
        >
          {isSelected && "✓ "}
          {type.name}
        </button>
      );
    });
  };

  // ── Favorites list (exclusive or featured) ───────────────────
  // Normalize whatever shape the slice stored — covers all known variants:
  // null | [] | Property[] | { properties: [] } | { data: { properties: [] } }
  const normalizeList = (data: any): any[] => {
    if (!data) return [];
    if (Array.isArray(data)) return data;                          // already an array
    if (Array.isArray(data?.properties)) return data.properties;   // { properties: [] }
    if (Array.isArray(data?.data?.properties)) return data.data.properties; // nested
    if (Array.isArray(data?.data)) return data.data;               // { data: [] }
    return [];
  };

  const activeProperties =
    normalizeList(exclusiveProperty);

  const renderFavoriteCard = (property: any) => {
    // API returns property_type and property_category as objects — extract name safely
    const typeName = typeof property.property_type === "object"
      ? property.property_type?.name
      : property.property_type;
    const categoryName = typeof property.property_category === "object"
      ? property.property_category?.name
      : property.property_category;
    const thumbnail = property.place_images?.[0] || property.cover_photo || property.image || null;
    const location = [property.city, property.country].filter(Boolean).join(", ") || "—";

    return (
      <Link
        key={property.id}
        to={`/listing-stay-detail/${property.slug || property.id}`}
        onClick={onClose}
        className="w-full flex items-center gap-3 pl-4 pr-2.5 py-2.5 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 hover:border-primary-600 dark:hover:border-primary-500 hover:shadow-md transition-all group overflow-visible"
      >
        {/* Thumbnail */}
        <div className="relative w-16 h-16 flex-shrink-0">
          <div className="w-16 h-16 rounded-md overflow-hidden bg-neutral-100 dark:bg-neutral-700">
            {thumbnail ? (
              <img
                src={thumbnail}
                alt={property.title || "Property"}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-neutral-400">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </div>
            )}
          </div>
          <span className="absolute -top-1.5 -left-1.5 text-[9px] font-bold bg-[#f5694b] text-white px-1.5 py-0.5 rounded-full shadow-sm whitespace-nowrap">★ Excl</span>
        </div>

        {/* Info */}
        <div className="flex-1 text-left min-w-0">
          <h4 className="font-semibold text-sm text-neutral-800 dark:text-neutral-100 truncate">
            {property.title || "Property"}
          </h4>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">{location}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {categoryName && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300">
                {categoryName}
              </span>
            )}
            {property.price_per_month && (
              <span className="text-sm font-bold text-primary-600 dark:text-primary-500">
                ${property.price_per_month}/mo
              </span>
            )}
          </div>
        </div>

        <svg className="w-5 h-5 text-neutral-400 dark:text-neutral-500 group-hover:text-primary-600 dark:group-hover:text-primary-500 transition-colors flex-shrink-0"
          fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </Link>
    );
  };

  return (
    <div className="w-full space-y-4">

      {/* Location Input */}
      <div className="relative z-50">
        <label className="block text-sm font-semibold text-neutral-800 dark:text-neutral-200 mb-2">
          Search by city or neighborhood
        </label>
        <LocationInputSimple
          value={selectedLocation}
          setValue={setSelectedLocation}
          propertyTypes={selectedRoomTypes}
          priceRange={priceRange}
          onSearchComplete={onClose}
          initialLat={latitude}
          initialLng={longitude}
          initialLocationData={locationData}
        />
        {/* {latitude && longitude && (
          <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
            📍 {latitude.toFixed(4)}, {longitude.toFixed(4)}
          </div>
        )} */}
      </div>

      {/* Property Type Pills */}
      <div className="pt-3 border-t border-neutral-200 dark:border-neutral-700">
        <span className="block text-sm font-semibold text-neutral-800 dark:text-neutral-200 mb-2">
          Property type
        </span>
        <div className="grid grid-cols-2 gap-2">
          {renderPropertyTypeButtons()}
        </div>
      </div>

      {/* Price Range */}
      <div className="pt-3 border-t border-neutral-200 dark:border-neutral-700">
        <button
          type="button"
          onClick={() => setShowPriceSlider(!showPriceSlider)}
          className="w-full flex items-center justify-between mb-2"
        >
          <span className="text-sm font-semibold text-neutral-800 dark:text-neutral-200">
            Price range
          </span>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-primary-600 dark:text-primary-500">
              ${priceRange[0]} - ${priceRange[1]}
            </span>
            <svg
              className={`w-5 h-5 text-neutral-500 transition-transform ${showPriceSlider ? "rotate-180" : ""}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>

        {showPriceSlider && (
          <div className="p-4 bg-neutral-50 dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700">
            <div className="relative h-8 flex items-center mb-4">
              <div className="absolute w-full h-2 bg-neutral-200 dark:bg-neutral-700 rounded-full" />
              <div
                className="absolute h-2 bg-primary-600 dark:bg-primary-500 rounded-full"
                style={{
                  left: `${(priceRange[0] / 5000) * 100}%`,
                  right: `${100 - (priceRange[1] / 5000) * 100}%`,
                }}
              />
              <input type="range" min="0" max="5000" step="50" value={priceRange[0]}
                onChange={(e) => handleRangeChange(e, true)}
                className="absolute w-full appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-primary-600 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md"
                style={{ zIndex: priceRange[0] > 4000 ? 5 : 3 }}
              />
              <input type="range" min="0" max="5000" step="50" value={priceRange[1]}
                onChange={(e) => handleRangeChange(e, false)}
                className="absolute w-full appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-primary-600 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md"
                style={{ zIndex: 4 }}
              />
            </div>
            <div className="flex items-center justify-between text-sm">
              <div className="text-center">
                <p className="text-xs text-neutral-500 dark:text-neutral-400">Min</p>
                <p className="font-bold text-neutral-800 dark:text-neutral-200">${priceRange[0]}</p>
              </div>
              <span className="text-neutral-400">—</span>
              <div className="text-center">
                <p className="text-xs text-neutral-500 dark:text-neutral-400">Max</p>
                <p className="font-bold text-neutral-800 dark:text-neutral-200">${priceRange[1]}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Curated Properties ── */}
      <div className="pt-3 border-t border-neutral-200 dark:border-neutral-700">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-neutral-800 dark:text-neutral-200">
            ★ Exclusive properties
          </h3>
        </div>

        {/* List */}
        {propertiesLoading ? (
          <div className="flex items-center justify-center py-6 gap-2 text-neutral-400">
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <span className="text-sm">Loading properties...</span>
          </div>
        ) : activeProperties.length > 0 ? (
          <div className="space-y-2">
            {activeProperties.slice(0, 4).map(renderFavoriteCard)}
          </div>
        ) : (
          <div className="text-center py-5 text-neutral-400 text-sm">
            No exclusive properties available right now.
          </div>
        )}

        {/* View all link */}
        {activeProperties.length > 0 && (
          <Link
            to="/exclusive"
            onClick={onClose}
            className="mt-3 flex items-center justify-center gap-1 text-xs text-primary-600 dark:text-primary-500 font-medium hover:underline"
          >
            View all properties
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        )}
      </div>

      {/* Active Filters */}
      {(selectedRoomTypes.length > 0 || priceRange[0] > 0 || priceRange[1] < 5000) && (
        <div className="pt-3 border-t border-neutral-200 dark:border-neutral-700">
          <span className="block text-sm font-semibold text-neutral-800 dark:text-neutral-200 mb-2">
            Active filters
          </span>
          <div className="flex items-center gap-2 flex-wrap">
            {selectedRoomTypes.map((type) => {
              const propertyType = propertyTypes?.find(
                (pt: any) => pt.name.toLowerCase() === type
              );
              return (
                <span key={type}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-semibold bg-primary-700 border-2 border-primary-700 text-white rounded-lg shadow-sm"
                >
                  {propertyType?.name || type}
                  <button type="button" onClick={(e) => handleRoomTypeClick(e, type)}
                    className="hover:bg-white/20 rounded-full p-0.5 transition-colors">×</button>
                </span>
              );
            })}
            {(priceRange[0] > 0 || priceRange[1] < 5000) && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-semibold bg-primary-700 border-2 border-primary-700 text-white rounded-lg shadow-sm">
                ${priceRange[0]}-${priceRange[1]}
                <button type="button" onClick={() => setPriceRange([0, 5000])}
                  className="hover:bg-white/20 rounded-full p-0.5 transition-colors">×</button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StaySearchFormMobileNR;